<template>
    <section class="ebc-panel">
        <div class="ebc-panel__head">
            <div class="ebc-segmented">
                <button
                    v-for="tab in tabs"
                    :key="tab.key"
                    type="button"
                    class="ebc-segmented__item"
                    :class="{ active: activeTab === tab.key }"
                    @click="activeTab = tab.key"
                >
                    {{ tab.label }}
                </button>
            </div>
            <button
                v-if="tableRows.length"
                v-b-tooltip.hover="{ title: '표로 보기', customClass: 'ebc-tooltip' }"
                type="button"
                class="table-open"
                @click="tableOpen = true"
            >
                <svg viewBox="0 0 16 16" aria-hidden="true">
                    <path d="M5 11L11 5M6 5h5v5" />
                </svg>
            </button>
        </div>

        <div class="ebc-panel__body">
            <template v-if="activeTab === 'trend'">
                <EChart v-if="trendRows.length" :option="trendOption" />
                <div v-else class="ebc-empty"><i class="fas fa-chart-bar"></i><span>사용량 이력이 없습니다</span></div>
            </template>

            <div v-else-if="activeTab === 'load'" class="load-grid">
                <div class="load-ratio">
                    <EChart v-if="hasLoadSection" :option="loadSectionOption" />
                    <div v-else class="ebc-empty">
                        <i class="fas fa-clock"></i><span>시간대별 요금 구분이 없는 요금제입니다</span>
                    </div>
                </div>
                <div v-if="analysisType === 'monthly'" class="load-profile">
                    <EChart v-if="grid" :option="profileOption" />
                    <div v-else class="ebc-empty">
                        <i :class="gridLoading ? 'fas fa-spinner fa-spin' : 'fas fa-wave-square'"></i>
                        <span>{{
                            gridLoading ? "시간대별 사용량을 불러오는 중..." : gridError || "시간대별 사용량이 없습니다"
                        }}</span>
                    </div>
                </div>
            </div>
        </div>

        <b-modal v-model="tableOpen" size="md" centered scrollable hide-footer body-class="p-0">
            <template #modal-header="{ close }">
                <h5 class="modal-title">{{ tableTitle }}</h5>
                <div class="table-modal__actions">
                    <button type="button" class="table-download" :disabled="downloading" @click="downloadExcel">
                        {{ downloading ? "다운로드 중..." : "엑셀 다운로드" }}
                    </button>
                    <button type="button" class="close" aria-label="Close" @click="close()">×</button>
                </div>
            </template>
            <div class="data-table-wrap">
            <table class="data-table ebc-num">
                <thead>
                    <tr>
                        <th v-for="col in tableColumns" :key="col.key" :class="{ 'is-num': col.num }">
                            {{ col.label }}
                        </th>
                    </tr>
                </thead>
                <tbody>
                    <tr v-for="(row, i) in tableRows" :key="i" :class="{ 'is-predict': row.predicted }">
                        <td
                            v-for="col in tableColumns"
                            :key="col.key"
                            :class="{ 'is-num': col.num }"
                            :style="col.key === 'label' && row.labelColor ? { color: row.labelColor } : null"
                        >
                            {{ row[col.key] }}
                        </td>
                    </tr>
                </tbody>
                <tfoot v-if="tableTotal">
                    <tr>
                        <td v-for="col in tableColumns" :key="col.key" :class="{ 'is-num': col.num }">
                            {{ tableTotal[col.key] }}
                        </td>
                    </tr>
                </tfoot>
            </table>
            </div>
        </b-modal>
    </section>
</template>

<script>
import EChart from "./EChart.vue";
import excelHelper from "@/libs/excelHelper.js";
import { chartColors, grayScale, navyScale, barGradient, tooltipStyle, tooltipHtml } from "../../utils/chartTheme.js";
import { axisLabel, axisLine, splitLine, legendText, dayLabelColor, seriesColors } from "./analysisChart.js";
import { getTimeSlotName } from "../../utils/helpers.js";
import { num, won, wonShort, ymShort, ymLabel, pct } from "./analysisFormat.js";

const SLOT_ORDER = ["LOW", "MID", "PEAK"];
const SLOT_COLORS = { LOW: seriesColors.soft, MID: seriesColors.accent, PEAK: seriesColors.primary };
const SLOT_LABEL_COLORS = { LOW: chartColors.text, MID: "#ffffff", PEAK: "#ffffff" };
const WEEKDAYS = ["일", "월", "화", "수", "목", "금", "토"];
/* 그라데이션 바는 echarts 기본 marker 가 색을 그리지 못해 직접 만든다 */
const barMarker = (color) =>
    `<span style="display:inline-block;width:9px;height:9px;border-radius:2px;background:${color};"></span>`;
const FEE_MARKER = barMarker(navyScale[1]);
const PREDICT_FEE_MARKER = barMarker(navyScale[4]);
const USAGE_MARKER = `<span style="display:inline-block;width:12px;height:2px;border-radius:1px;background:${grayScale[1]};"></span>`;

export default {
    name: "UsagePatternPanel",
    components: { EChart },
    props: {
        analysis: {
            type: Object,
            required: true,
        },
        analysisType: {
            type: String,
            default: "monthly",
        },
        grid: {
            type: Object,
            default: null,
        },
        gridLoading: {
            type: Boolean,
            default: false,
        },
        gridError: {
            type: String,
            default: "",
        },
    },
    data() {
        return {
            activeTab: "trend",
            tableOpen: false,
            downloading: false,
        };
    },
    computed: {
        unit() {
            return this.analysis.contractInfo?.rawUnit || "kWh";
        },
        loadSection() {
            return this.analysis.loadSectionCostRatio || null;
        },
        hasLoadSection() {
            return !!this.loadSection && SLOT_ORDER.some((slot) => (this.loadSection[slot]?.rawUsage || 0) > 0);
        },
        tabs() {
            const tabs = [{ key: "trend", label: this.analysisType === "yearly" ? "월별 추이" : "일별 추이" }];
            if (this.analysisType === "monthly" || this.hasLoadSection)
                tabs.push({ key: "load", label: "시간대 부하" });
            return tabs;
        },
        trendRows() {
            const trend = this.analysis.usageTrend || {};
            const rows = this.analysisType === "yearly" ? trend.monthly || [] : trend.daily || [];
            return rows.filter((row) => row.rawUsage != null || row.predict);
        },
        trendOption() {
            const yearly = this.analysisType === "yearly";
            const rows = this.trendRows;
            const unit = this.unit;
            const labels = rows.map((row) => (yearly ? ymShort(row.regYm) : `${new Date(row.date).getDate()}일`));
            const labelColor = (value, index) => {
                const row = rows[index];
                return yearly || !row ? grayScale[0] : dayLabelColor(row.date, row.isHoliday);
            };
            return {
                backgroundColor: "transparent",
                grid: { left: 8, right: 8, top: 28, bottom: 4, containLabel: true },
                legend: { top: 0, right: 0, itemWidth: 10, itemHeight: 8, textStyle: legendText() },
                tooltip: tooltipStyle({
                    trigger: "axis",
                    axisPointer: { type: "shadow" },
                    appendTo: "body",
                    formatter: (params) => {
                        const row = rows[params[0]?.dataIndex];
                        if (!row) return "";
                        let title = yearly ? ymLabel(row.regYm) : row.date;
                        if (!yearly) {
                            const d = new Date(row.date);
                            title += ` (${WEEKDAYS[d.getDay()]}${row.isHoliday ? "·공휴일" : ""})`;
                        }
                        let html = tooltipHtml.title(title) + tooltipHtml.bodyOpen();
                        if (row.totalFee != null) html += tooltipHtml.row(FEE_MARKER, "요금", won(row.totalFee));
                        if (row.predict)
                            html += tooltipHtml.row(PREDICT_FEE_MARKER, "예측 요금", won(row.predict.totalFee));
                        if (row.rawUsage != null)
                            html += tooltipHtml.row(USAGE_MARKER, "사용량", `${num(row.rawUsage)} ${unit}`);
                        if (row.predict)
                            html += tooltipHtml.row(
                                USAGE_MARKER,
                                "예측 사용량",
                                `${num(row.predict.rawUsage)} ${unit}`,
                            );
                        return html + tooltipHtml.bodyClose();
                    },
                }),
                xAxis: {
                    type: "category",
                    data: labels,
                    axisLabel: axisLabel({ color: labelColor }),
                    axisLine: axisLine(),
                    axisTick: { show: false },
                },
                yAxis: [
                    { type: "value", axisLabel: axisLabel({ formatter: (v) => wonShort(v) }), splitLine: splitLine() },
                    { type: "value", axisLabel: axisLabel({ formatter: (v) => num(v) }), splitLine: { show: false } },
                ],
                series: [
                    {
                        name: "요금",
                        type: "bar",
                        stack: "fee",
                        barMaxWidth: 28,
                        barCategoryGap: "15%",
                        itemStyle: { color: barGradient(navyScale[1], 0.35), borderRadius: [3, 3, 0, 0] },
                        data: rows.map((row) => (row.totalFee == null ? null : row.totalFee)),
                    },
                    {
                        name: "예측 요금",
                        type: "bar",
                        stack: "fee",
                        barMaxWidth: 28,
                        itemStyle: { color: navyScale[4], borderRadius: [3, 3, 0, 0] },
                        data: rows.map((row) => (row.totalFee == null && row.predict ? row.predict.totalFee : null)),
                    },
                    {
                        name: `사용량 (${unit})`,
                        type: "line",
                        yAxisIndex: 1,
                        smooth: true,
                        symbol: "none",
                        lineStyle: { width: 1.5, color: grayScale[1] },
                        itemStyle: { color: grayScale[1] },
                        data: rows.map((row) =>
                            row.rawUsage != null ? row.rawUsage : (row.predict?.rawUsage ?? null),
                        ),
                    },
                ],
            };
        },
        tableTitle() {
            const tab = this.tabs.find((t) => t.key === this.activeTab);
            const first = this.trendRows[0];
            let period = "";
            if (first && this.analysisType === "yearly") period = String(first.regYm).substring(0, 4) + "년";
            else if (first?.date) {
                const d = new Date(first.date);
                period = `${d.getFullYear()}년 ${d.getMonth() + 1}월`;
            }
            const label = tab ? tab.label : "";
            return period ? `${label} (${period})` : label;
        },
        tableColumns() {
            if (this.activeTab === "load")
                return [
                    { key: "label", label: "시간대" },
                    { key: "usage", label: `사용량 (${this.unit})`, num: true },
                    { key: "usageRatio", label: "사용량 비중", num: true },
                    { key: "fee", label: "요금 (원)", num: true },
                    { key: "feeRatio", label: "요금 비중", num: true },
                ];
            return [
                { key: "label", label: this.analysisType === "yearly" ? "월" : "일자" },
                { key: "usage", label: `사용량 (${this.unit})`, num: true },
                { key: "fee", label: "요금 (원)", num: true },
            ];
        },
        tableRows() {
            if (this.activeTab === "load") {
                if (!this.hasLoadSection) return [];
                const ls = this.loadSection;
                return SLOT_ORDER.filter((slot) => ls[slot]).map((slot) => ({
                    label: getTimeSlotName(slot),
                    usage: num(ls[slot].rawUsage),
                    usageRatio: pct(ls[slot].usageRatio),
                    fee: num(ls[slot].cost),
                    feeRatio: pct(ls[slot].costRatio),
                    rawUsage: ls[slot].rawUsage || 0,
                    rawFee: ls[slot].cost || 0,
                    rawUsageRatio: ls[slot].usageRatio || 0,
                    rawFeeRatio: ls[slot].costRatio || 0,
                }));
            }
            const yearly = this.analysisType === "yearly";
            return this.trendRows.map((row) => {
                const predicted = row.totalFee == null && !!row.predict;
                const usage = row.rawUsage != null ? row.rawUsage : row.predict?.rawUsage;
                const fee = predicted ? row.predict.totalFee : row.totalFee;
                let label = yearly ? ymLabel(row.regYm) : row.date;
                if (!yearly) {
                    const d = new Date(row.date);
                    label += ` (${WEEKDAYS[d.getDay()]}${row.isHoliday ? "·공휴일" : ""})`;
                }
                return {
                    label,
                    labelColor: yearly ? null : dayLabelColor(row.date, row.isHoliday),
                    usage: usage == null ? "-" : num(usage),
                    fee: fee == null ? "-" : num(fee),
                    predicted,
                    rawUsage: usage || 0,
                    rawFee: fee || 0,
                };
            });
        },
        tableTotal() {
            if (this.activeTab === "load") {
                const ls = this.loadSection || {};
                const slots = SLOT_ORDER.map((slot) => ls[slot]).filter(Boolean);
                if (!slots.length) return null;
                return {
                    label: "합계",
                    usage: num(slots.reduce((sum, s) => sum + (s.rawUsage || 0), 0)),
                    usageRatio: "100%",
                    fee: num(slots.reduce((sum, s) => sum + (s.cost || 0), 0)),
                    feeRatio: "100%",
                };
            }
            if (!this.tableRows.length) return null;
            return {
                label: "합계",
                usage: num(this.tableRows.reduce((sum, row) => sum + row.rawUsage, 0)),
                fee: num(this.tableRows.reduce((sum, row) => sum + row.rawFee, 0)),
            };
        },
        loadSectionOption() {
            const ls = this.loadSection || {};
            return {
                backgroundColor: "transparent",
                grid: { left: 8, right: 16, top: 28, bottom: 4, containLabel: true },
                legend: { top: 0, left: 0, itemWidth: 10, itemHeight: 8, textStyle: legendText() },
                tooltip: tooltipStyle({
                    trigger: "axis",
                    axisPointer: { type: "shadow" },
                    appendTo: "body",
                    formatter: (params) => {
                        const slot = ["사용량 비중", "요금 비중"][params[0]?.dataIndex];
                        let html = tooltipHtml.title(slot) + tooltipHtml.bodyOpen();
                        params.forEach((p) => {
                            const s = ls[SLOT_ORDER[p.seriesIndex]] || {};
                            const detail = p.dataIndex === 0 ? `${num(s.rawUsage)} ${this.unit}` : won(s.cost);
                            html += tooltipHtml.row(p.marker, p.seriesName, `${pct(p.value)} · ${detail}`);
                        });
                        return html + tooltipHtml.bodyClose();
                    },
                }),
                xAxis: {
                    type: "value",
                    max: 100,
                    axisLabel: axisLabel({ formatter: (v) => `${v}%` }),
                    splitLine: splitLine(),
                },
                yAxis: {
                    type: "category",
                    inverse: true,
                    data: ["사용량", "요금"],
                    axisLabel: axisLabel(),
                    axisLine: { show: false },
                    axisTick: { show: false },
                },
                series: SLOT_ORDER.map((slot) => ({
                    name: getTimeSlotName(slot),
                    type: "bar",
                    stack: "ratio",
                    barMaxWidth: 26,
                    itemStyle: { color: SLOT_COLORS[slot] },
                    label: {
                        show: true,
                        color: SLOT_LABEL_COLORS[slot],
                        fontSize: 10,
                        formatter: (p) => (p.value >= 8 ? `${p.value.toFixed(0)}%` : ""),
                    },
                    data: [ls[slot]?.usageRatio || 0, ls[slot]?.costRatio || 0],
                })),
            };
        },
        profileOption() {
            const rows = this.grid?.grid || [];
            const days = Math.max(1, (this.grid?.columns || []).length);
            const unit = this.grid?.unit || this.unit;
            return {
                backgroundColor: "transparent",
                grid: { left: 8, right: 8, top: 24, bottom: 4, containLabel: true },
                title: {
                    text: "시간대별 평균 사용량",
                    left: 0,
                    top: 0,
                    textStyle: { fontSize: 11, fontWeight: 600, color: grayScale[0] },
                },
                tooltip: tooltipStyle({
                    trigger: "axis",
                    appendTo: "body",
                    formatter: (params) =>
                        tooltipHtml.title(params[0].axisValue) +
                        tooltipHtml.bodyOpen() +
                        tooltipHtml.row("", "평균", `${num(params[0].value, 1)} ${unit}`) +
                        tooltipHtml.bodyClose(),
                }),
                xAxis: {
                    type: "category",
                    data: rows.map((r) => `${r.hour}시`),
                    axisLabel: axisLabel(),
                    axisLine: axisLine(),
                    axisTick: { show: false },
                },
                yAxis: { type: "value", axisLabel: axisLabel({ formatter: (v) => num(v) }), splitLine: splitLine() },
                series: [
                    {
                        type: "line",
                        smooth: true,
                        symbol: "none",
                        lineStyle: { width: 2, color: seriesColors.primary },
                        areaStyle: { color: seriesColors.primary, opacity: 0.12 },
                        data: rows.map((r) => Math.round(((r.total || 0) / days) * 10) / 10),
                    },
                ],
            };
        },
    },
    methods: {
        async downloadExcel() {
            const cols = this.tableColumns;
            const rawOf = { usage: "rawUsage", fee: "rawFee", usageRatio: "rawUsageRatio", feeRatio: "rawFeeRatio" };
            const toRecord = (row, isTotal) =>
                Object.fromEntries(
                    cols.map((col) => {
                        if (!col.num) return [col.label, row[col.key]];
                        if (isTotal) return [col.label, col.key.endsWith("Ratio") ? 100 : row[rawOf[col.key]]];
                        return [col.label, Math.round((row[rawOf[col.key]] || 0) * 10) / 10];
                    }),
                );
            const rows = this.tableRows.map((row) => toRecord(row, false));
            const total = {
                label: "합계",
                rawUsage: this.tableRows.reduce((sum, row) => sum + (row.rawUsage || 0), 0),
                rawFee: this.tableRows.reduce((sum, row) => sum + (row.rawFee || 0), 0),
            };
            rows.push(toRecord(total, true));
            const cellTypes = Object.fromEntries(cols.filter((col) => col.num).map((col) => [col.label, "number"]));
            this.downloading = true;
            try {
                await excelHelper.createAndDownloadWorkbook(
                    rows,
                    cols.map((col) => col.label),
                    `${this.tableTitle.replace(/\s*\((.*)\)/, "_$1").replace(/\s+/g, "")}.xlsx`,
                    { sheetName: this.tableTitle.replace(/\s*\(.*\)/, ""), cellTypes, freezeHeader: true, columnWidths: { 일자: 20, 월: 14 } },
                );
            } finally {
                this.downloading = false;
            }
        },
    },
    watch: {
        analysisType() {
            if (!this.tabs.some((tab) => tab.key === this.activeTab)) this.activeTab = "trend";
        },
    },
};
</script>

<style scoped>
.table-open {
    display: inline-flex;
    flex-shrink: 0;
    align-items: center;
    justify-content: center;
    width: 26px;
    height: 26px;
    margin-left: auto;
    padding: 0;
    color: var(--ebc-text-sub);
    background: transparent;
    border: 0;
    border-radius: var(--ebc-radius-sm);
    cursor: pointer;
}

.table-open:hover {
    color: var(--ebc-primary);
    background: var(--ebc-primary-tint);
}

.table-open:focus {
    outline: none;
}

.table-open svg {
    width: 16px;
    height: 16px;
    fill: none;
    stroke: currentColor;
    stroke-width: 1.6;
    stroke-linecap: round;
    stroke-linejoin: round;
}

.table-modal__actions {
    display: flex;
    align-items: center;
    gap: 12px;
    margin-left: auto;
}

.table-download {
    padding: 4px 12px;
    font-size: 12px;
    font-weight: 600;
    color: #475569;
    background: #f1f5f9;
    border: 1px solid #e2e8f0;
    border-radius: 999px;
    cursor: pointer;
}

.table-download:hover:not(:disabled) {
    color: #13203a;
    background: #e2e8f0;
}

.table-download:disabled {
    cursor: default;
    opacity: 0.6;
}

.table-download:focus {
    outline: none;
}

.table-modal__actions .close {
    margin: 0;
    padding: 0;
}

.data-table-wrap {
    padding: 0 16px 4px;
}

.data-table {
    width: 100%;
    font-size: 12px;
    border-collapse: separate;
    border-spacing: 0;
}

.data-table th,
.data-table td {
    padding: 7px 12px;
    white-space: nowrap;
}

.data-table td {
    color: #334155;
    border-bottom: 1px solid #eef2f6;
}

.data-table th {
    position: sticky;
    z-index: 1;
    top: 0;
    padding-top: 12px;
    font-size: 11px;
    font-weight: 600;
    color: #64748b;
    background: #fff;
    box-shadow: inset 0 -1px 0 #d0d9e2;
}

.data-table .is-num {
    text-align: right;
}

.data-table tr.is-predict td {
    color: #9aa7b7;
}

.data-table tfoot td {
    position: sticky;
    z-index: 1;
    bottom: 0;
    padding-bottom: 12px;
    font-weight: 700;
    color: #13203a;
    background: #fff;
    border-bottom: 0;
    box-shadow: inset 0 1px 0 #d0d9e2;
}

.load-grid {
    height: 100%;
    display: grid;
    grid-template-columns: minmax(0, 1fr) minmax(0, 1.4fr);
    gap: 16px;
}

.load-ratio,
.load-profile {
    min-width: 0;
    min-height: 0;
}
</style>
